'use client';

import { loadStoryblokBridge, StoryblokBridgeConfigV2 } from '@storyblok/js';
import { useRouter } from 'next/navigation';
import { startTransition, useEffect } from 'react';

interface StoryblokBridgeLoaderProps {
  options: StoryblokBridgeConfigV2;
}

export default function StoryblokBridge({ options }: StoryblokBridgeLoaderProps) {
  const router = useRouter();

  useEffect(() => {
    let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
    let bridge: InstanceType<typeof window.StoryblokBridge> | null = null;
    let isCancelled = false;

    const refresh = () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }

      refreshTimeout = setTimeout(() => {
        startTransition(() => {
          router.refresh();
        });
      }, 150);
    };

    const initBridge = () => {
      if (isCancelled || typeof window.StoryblokBridge !== 'function') {
        return;
      }

      bridge = new window.StoryblokBridge(options);
      bridge.on('input', refresh);
      bridge.on(['change', 'published'], () => {
        window.location.reload();
      });
    };

    const registerBridge = async () => {
      await loadStoryblokBridge();

      if (isCancelled || typeof window.StoryblokBridge !== 'function') {
        return;
      }

      initBridge();
    };

    void registerBridge();

    return () => {
      isCancelled = true;
      bridge = null;

      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [options, router]);

  return null;
}
